/**
 * v4.32 - 2026-10-20 - AIRBYTE-CONNECTOR-ROLLOUT-01 (F1, F3): the sign-in type comes from the server's projected
 *                      authType with no default; oauth2 opens the provider window, api_key / basic the form, anything
 *                      else (absent, unknown, or a form type whose form is unavailable) an error and no window. The
 *                      popup error plan_limit_reached shows the plan-limit sentence.
 * v4.28 - 2026-10-13 - G10-B (UI-PERMISSION-LIST-NOT-ENFORCED-01): connector cards act only with manageConnections; Finish Setup is shown only with runPipeline (server permission map)
 * DataSourceConnect - Dynamic connector selection component
 * v4.26 - 2026-10-06 - INDUSTRY-CODE-NOT-STORED (S8; F11): a Finish Setup that stops on INDUSTRY_CODE_MISSING also
 *                      calls the optional onNeedIndustry, so the page can show the industry screen
 * v4.22 - 2026-10-02 - CONNECTOR-HEALTH-STATE-H3 (H3-9; F85): per-type health dot and its 60 s poll removed
 * v4.21 - 2026-10-01 - CONNECTOR-HEALTH-STATE-H2 (F16, F82): badge, caption and border from the server verdict
 *                      (utils/connectionHealthDisplay); Finish Setup not-ready decided by the shared helper over the
 *                      pending connections' verdicts (AIRBYTE_SYNC_FAILED stops), with a per-connection list
 * v3.62 - 2026-04-05 - EXCEL-01 P5: Excel/CSV upload card + dialog wired to ExcelUpload component
 * v3.61 - 2026-03-06 - DEMO-01: demo connection limit enforcement (UI-only, full catalog visible)
 * v3.60 - 2026-03-04 - Added "Industry Fit" chip for connectors with priorityScore > 0 (NAICS-aware RAG)
 * v3.15 - 2026-02-15 - Fix #3: Resilient OAuth popup — shows retry only after 30s timeout, not unconditional refresh
 * v3.00 - 2026-02-07 - Connector health status dots with 60s polling
 * v2.81 - Fix popup OAuth not refreshing connector list on return - 2026-02-05
 * v2.80 - OAuth opens in popup window to keep browser history clean - 2026-02-05
 * v2.34 - Added Finish Setup button to trigger pipeline - 2026-01-31
 * v2.26 - Added API Key/Basic Auth form modals and delegation - 2026-01-30
 * v2.23 - Added connectorStatus and connection display - 2026-01-30
 *
 * Displays connectors in three sections:
 * 1. Matched - Connectors matching customer's discovery dataSources (large icons)
 * 2. Recommended - Popular for SMB or matching industry (medium icons)
 * 3. Other - Remaining connectors grouped by category (small icons)
 *
 * Now shows:
 * - Connected status with green checkmark
 * - Coming Soon badge for unavailable connectors
 * - Sync status for connected sources
 * - Form modal for API Key/Basic Auth connectors
 * - Delegation option to send connection link to accountant
 * - Finish Setup button when at least one source is connected (v2.34)
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  CircularProgress,
  Collapse,
  Chip,
  Skeleton,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  InputAdornment,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PaymentIcon from '@mui/icons-material/Payment';
import PeopleIcon from '@mui/icons-material/People';
import CampaignIcon from '@mui/icons-material/Campaign';
import CloudIcon from '@mui/icons-material/Cloud';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SyncIcon from '@mui/icons-material/Sync';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { Tooltip } from '@mui/material';
import { connectionsService, connectorsService, authService, setupService, ConnectorInfo, PreAuthParam, FormSchema } from '../services/api';
import CredentialFormModal from './CredentialFormModal';
import DelegationModal from './DelegationModal';
import ContactFormModal from './ContactFormModal'; // v3.85 - MKTG-03: replaces mailto:support
import { useClerkAuth } from '../App'; // v3.61 - DEMO-01: access isDemo + demoMaxConnections
import {
  healthDisplay, finishSetupDecision, HealthCarrier, HealthState, PendingConnectionView,
} from '../utils/connectionHealthDisplay'; // v4.21 - CONNECTOR-HEALTH-STATE-H2
import { usePermissions } from '../hooks/usePermissions'; // v4.28 - G10-B

// Icon mapping from Cosmos DB icon names to MUI components
const iconMap: Record<string, React.ReactNode> = {
  'Storefront': <StorefrontIcon />,
  'AccountBalance': <AccountBalanceIcon />,
  'PointOfSale': <PointOfSaleIcon />,
  'Payment': <PaymentIcon />,
  'People': <PeopleIcon />,
  'Campaign': <CampaignIcon />,
  'Cloud': <CloudIcon />,
  'Email': <EmailIcon />,
};

// v4.32 - 2026-10-20 - AIRBYTE-CONNECTOR-ROLLOUT-01 (F1, F3): customer sentences for the two refusals below.
const FORM_UNAVAILABLE_MESSAGE = 'This connection form is not available right now. Please contact support.';
const PLAN_LIMIT_ERROR = 'plan_limit_reached';
const PLAN_LIMIT_MESSAGE = "Your plan's connection limit is reached. Upgrade your plan to add more connections.";

const getIcon = (iconName: string, size: number = 40): React.ReactNode => {
  const IconComponent = iconMap[iconName];
  if (IconComponent) {
    return React.cloneElement(IconComponent as React.ReactElement, { sx: { fontSize: size } });
  }
  return <BusinessIcon sx={{ fontSize: size }} />;
};

// Category display names
const categoryLabels: Record<string, string> = {
  'accounting': 'Accounting',
  'ecommerce': 'E-Commerce',
  'payments': 'Payments',
  'hr': 'HR & Payroll',
  'crm': 'CRM',
  'marketing': 'Marketing',
  'other': 'Other',
};

// v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-3b, F16): the v2.23 getSyncStatusDisplay helper (which returned nothing for
// setup_failed / pending_setup while the badge stayed green) is replaced by utils/connectionHealthDisplay.
const healthBadgeIcon = (state: HealthState): React.ReactElement | undefined => {
  switch (state) {
    case 'healthy': return <CheckCircleIcon />;
    case 'needs_attention': return <ErrorIcon />;
    case 'syncing':
    case 'setting_up': return <SyncIcon />;
    default: return undefined;
  }
};

interface DataSourceConnectProps {
  tenantId: string;
  onConnect: (jobId?: string) => void;
  onSkip: () => void;
  onNeedIndustry?: () => void; // v4.26 - INDUSTRY-CODE-NOT-STORED S8
}

const DataSourceConnect: React.FC<DataSourceConnectProps> = ({
  tenantId,
  onConnect,
  onSkip,
  onNeedIndustry,
}) => {
  // v4.28 - G10-B (UI-PERMISSION-LIST-NOT-ENFORCED-01): connecting and Finish Setup follow the server's permission map
  const { hasPermission } = usePermissions();
  const canManageConnections = hasPermission('manageConnections');
  const canRunPipeline = hasPermission('runPipeline');
  const [connecting, setConnecting] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOther, setShowOther] = useState(false);
  // v3.15 - Fix #3 resilience: stale popup detector shows retry only after timeout
  const [popupStale, setPopupStale] = useState(false);
  const popupRef = React.useRef<Window | null>(null);
  const popupTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  // v3.61 - DEMO-01: demo limits for connector gating (UI-only, full catalog still visible)
  const { isDemo, demoMaxConnections } = useClerkAuth();
  // v3.85 - MKTG-03: contact modal state (replaces mailto:support connector request)
  const [connectorRequestOpen, setConnectorRequestOpen] = useState(false);
  // v3.95 - Pre-auth modal state (Shopify store picker, etc.) - 2026-04-06
  const [preAuthModalOpen, setPreAuthModalOpen] = useState(false);
  const [preAuthConnector, setPreAuthConnector] = useState<ConnectorInfo | null>(null);
  const [preAuthValues, setPreAuthValues] = useState<Record<string, string>>({});
  const [preAuthErrors, setPreAuthErrors] = useState<Record<string, string>>({});

  // Connector data from API
  const [matched, setMatched] = useState<ConnectorInfo[]>([]);
  const [recommended, setRecommended] = useState<ConnectorInfo[]>([]);
  const [other, setOther] = useState<Record<string, ConnectorInfo[]>>({});

  // v2.26 - Credential form modal state
  const [credentialModalOpen, setCredentialModalOpen] = useState(false);
  const [credentialFormSchema, setCredentialFormSchema] = useState<FormSchema | null>(null);
  const [credentialConnector, setCredentialConnector] = useState<ConnectorInfo | null>(null);
  const [credentialLoading, setCredentialLoading] = useState(false);
  const [credentialError, setCredentialError] = useState<string | null>(null);

  // v2.26 - Delegation modal state
  const [delegationModalOpen, setDelegationModalOpen] = useState(false);
  const [delegationConnector, setDelegationConnector] = useState<ConnectorInfo | null>(null);

  // v2.34 - Finish Setup state
  const [finishingSetup, setFinishingSetup] = useState(false);
  const [finishError, setFinishError] = useState<string | null>(null);
  // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-6b): the pending connections behind a not-ready answer, with their verdicts
  const [finishPending, setFinishPending] = useState<PendingConnectionView[]>([]);

  useEffect(() => {
    loadConnectors();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // v2.84 - Listen for OAuth popup completion message - 2026-02-05
  // Accept messages from both mylojic.com and www.mylojic.com (origin mismatch fix)
  useEffect(() => {
    const handleOAuthMessage = (event: MessageEvent) => {
      // v2.84 - Accept both www and non-www origins (backend redirects to www)
      const validOrigins = [window.location.origin, 'https://mylojic.com', 'https://www.mylojic.com'];
      if (!validOrigins.includes(event.origin)) return;
      if (event.data?.type === 'oauth_success') {
        console.log('v2.84: OAuth popup success:', event.data.connector, event.data.connectionId);
        setConnecting(null);
        setPopupStale(false); // v3.15 - Clear stale state on success
        // v3.15 - Clear stale popup timer on success
        if (popupTimerRef.current) { clearTimeout(popupTimerRef.current); popupTimerRef.current = null; }
        // Small delay to ensure backend has written connection record before we fetch
        setTimeout(() => {
          loadConnectors();
        }, 1000);
      } else if (event.data?.type === 'oauth_error') {
        // v4.19 - AIRBYTE-BRIDGE-PLATFORM-CREDENTIALS S3 (F04): surface the failure
        // instead of leaving the tile spinning as if it succeeded.
        console.log('v4.19: OAuth popup error:', event.data.error);
        setConnecting(null);
        setPopupStale(false);
        if (popupTimerRef.current) { clearTimeout(popupTimerRef.current); popupTimerRef.current = null; }
        // v4.32 - AIRBYTE-CONNECTOR-ROLLOUT-01 (F3): the plan-limit refusal is named to the customer.
        setError(event.data.error === PLAN_LIMIT_ERROR ? PLAN_LIMIT_MESSAGE
          : "We couldn't finish connecting your account. Please try again.");
      }
    };

    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // v3.95 - Resilient connector loading: handles both grouped (BFF) and flat (direct) response shapes.
  // BFF returns {matched, recommended, other} from /api/connectors/for-tenant/{id}/with-connections.
  // Direct d75-agent returns {connectors: [...]} from /api/connectors (flat catalog).
  // When BFF is bypassed (Front Door routing), fall back to grouping flat catalog by category
  // with popularForSmb connectors promoted to recommended. - 2026-03-30
  const loadConnectors = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: any = await connectorsService.getForTenant();

      // v3.97 - 2026-03-31 - Merge per-tenant connections into catalog
      // Direct d75-agent /api/connectors returns catalog only (no connection status).
      // BFF used to merge via for-tenant/{id}/with-connections. Without BFF, we fetch
      // /api/connections separately and merge by sourceType === connectorType.
      let connectionMap: Record<string, any> = {};
      try {
        const connRes = await connectionsService.list(tenantId);
        for (const conn of connRes.connections || []) {
          connectionMap[conn.sourceType] = conn;
        }
      } catch (e) {
        // non-critical — catalog still loads without connection status
      }
      const mergeConn = (c: ConnectorInfo): ConnectorInfo => ({
        ...c,
        connection: connectionMap[c.connectorType] ?? c.connection ?? null,
      });

      if (response.matched || response.recommended || response.other) {
        // Grouped response from BFF → for-tenant endpoint
        setMatched((response.matched || []).map(mergeConn));
        setRecommended((response.recommended || []).map(mergeConn));
        setOther(Object.fromEntries(
          Object.entries(response.other || {}).map(([k, v]) => [k, (v as ConnectorInfo[]).map(mergeConn)])
        ));
      } else if (response.connectors && Array.isArray(response.connectors)) {
        // Flat response from direct d75-agent → group by category client-side
        const rec: ConnectorInfo[] = [];
        const grouped: Record<string, ConnectorInfo[]> = {};
        for (const conn of response.connectors) {
          // Normalize: flat response lacks connectorStatus/connection fields
          const normalized: ConnectorInfo = {
            ...conn,
            connectorStatus: conn.connectorStatus || 'available',
            connection: connectionMap[conn.connectorType] ?? conn.connection ?? null,
          };
          if (conn.popularForSmb) {
            rec.push(normalized);
          } else {
            const cat = conn.category || 'Other';
            if (!grouped[cat]) grouped[cat] = [];
            grouped[cat].push(normalized);
          }
        }
        setMatched([]);
        setRecommended(rec);
        setOther(grouped);
      }
    } catch (err: any) {
      console.error('Failed to load connectors:', err);
      setError(err.userMessage || 'Failed to load data sources');
    } finally {
      setLoading(false);
    }
  };

  // v2.26 - Updated to handle different auth types
  const handleConnect = async (connector: ConnectorInfo) => {
    // Don't allow connecting to coming_soon or maintenance connectors
    if (connector.connectorStatus === 'coming_soon' || connector.connectorStatus === 'maintenance') {
      return;
    }

    setConnecting(connector.connectorType);
    setError(null);
    // v3.96 - 2026-03-31 - Flag prevents finally from clearing connecting state for OAuth popup path
    // (finally always runs even after return, which was immediately clearing the spinner)
    let isOAuthPopup = false;

    try {
      // Check auth type to determine flow
      // v4.32 - AIRBYTE-CONNECTOR-ROLLOUT-01 (F1): the server's projected type, no default (was || 'oauth2').
      const authType = connector.authType?.toLowerCase();

      if (authType === 'api_key' || authType === 'basic') {
        // Fetch form schema and show modal
        const formResponse = await authService.getFormSchema(connector.connectorType);
        if (formResponse.flow_type === 'form' && formResponse.form_schema) {
          setCredentialFormSchema(formResponse.form_schema);
          setCredentialConnector(connector);
          setCredentialModalOpen(true);
        } else {
          // v4.32 - ACR-01 (F1): no provider window for a form connector (was a popup fallback).
          setError(FORM_UNAVAILABLE_MESSAGE);
        }
      } else if (authType !== 'oauth2') {
        // v4.32 - ACR-01 (F1): absent or unknown sign-in type — never open a provider window.
        setError(FORM_UNAVAILABLE_MESSAGE);
      } else if (connector.preAuthParams && connector.preAuthParams.length > 0) {
        // v3.95 - Pre-auth params required: show modal to collect input before OAuth - 2026-04-06
        setPreAuthConnector(connector);
        setPreAuthValues({});
        setPreAuthErrors({});
        setPreAuthModalOpen(true);
        return;
      } else {
        // v2.80 - OAuth flow in popup window (keeps main window history clean)
        isOAuthPopup = true;
        const popup = connectionsService.startOAuthPopup(connector.connectorType, tenantId);
        // v3.15 - Fix #3 resilience: store popup ref, reset stale state
        popupRef.current = popup;
        setPopupStale(false);
        if (popupTimerRef.current) { clearTimeout(popupTimerRef.current); popupTimerRef.current = null; }
        if (popup) {
          // v3.13 - Monitor popup health — detect if it closes without success
          const checkInterval = setInterval(() => {
            if (popup.closed) {
              clearInterval(checkInterval);
              setPopupStale(false); // v3.15 - Clear stale if popup closed
              if (popupTimerRef.current) { clearTimeout(popupTimerRef.current); popupTimerRef.current = null; }
              setConnecting(prev => {
                if (prev === connector.connectorType) {
                  console.log('v3.13: OAuth popup closed without success message');
                  return null;
                }
                return prev;
              });
            }
          }, 1000);
          // v3.15 - Fix #3 resilience: after 30s with no success and popup still open,
          // show "Taking too long?" retry option — only under that condition
          popupTimerRef.current = setTimeout(() => {
            if (!popup.closed) {
              console.log('v3.15: OAuth popup still open after 30s — showing retry option');
              setPopupStale(true);
            }
          }, 30000);
        }
        // Don't clear connecting state - will be cleared when popup message received
        return;
      }
    } catch (err: any) {
      console.error('Failed to start connection:', err);
      setError(err.userMessage || 'Failed to start connection');
    } finally {
      // v3.96 - Only clear connecting for non-popup paths; OAuth popup clears it via postMessage or popup-closed handler
      if (!isOAuthPopup) setConnecting(null);
    }
  };

  // v2.26 - Handle credential form submission
  const handleCredentialSubmit = async (data: Record<string, string>) => {
    if (!credentialConnector) return;

    setCredentialLoading(true);
    setCredentialError(null);

    try {
      const result = await authService.submitCredentials(credentialConnector.connectorType, {
        ...data,
        tenant_id: tenantId,
      });

      if (result.success) {
        setCredentialModalOpen(false);
        setCredentialFormSchema(null);
        setCredentialConnector(null);
        // Reload connectors to show new connection
        loadConnectors();
      } else {
        setCredentialError(result.error || 'Failed to save credentials');
      }
    } catch (err: any) {
      console.error('Credential submission failed:', err);
      setCredentialError(err.userMessage || 'Failed to save credentials');
    } finally {
      setCredentialLoading(false);
    }
  };

  // v2.26 - Handle delegation click from credential form
  const handleDelegateFromForm = () => {
    if (credentialConnector) {
      setCredentialModalOpen(false);
      setDelegationConnector(credentialConnector);
      setDelegationModalOpen(true);
    }
  };

  // v2.26 - Handle delegation for OAuth connectors
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDelegateClick = (connector: ConnectorInfo) => {
    setDelegationConnector(connector);
    setDelegationModalOpen(true);
  };

  // v2.26 - Send delegation request
  const handleSendDelegation = async (email: string, message?: string) => {
    if (!delegationConnector) {
      return { success: false, error: 'No connector selected' };
    }

    try {
      const result = await authService.createDelegation({
        connectorType: delegationConnector.connectorType,
        delegateEmail: email,
        message,
      });
      return result;
    } catch (err: any) {
      console.error('Delegation failed:', err);
      return { success: false, error: err.userMessage || 'Failed to send delegation request' };
    }
  };

  // v3.95 - Pre-auth modal: normalize input (strip protocol/suffix/path) then validate - 2026-04-06
  const normalizePreAuthValue = (value: string, param: PreAuthParam): string => {
    let v = value.trim();
    v = v.replace(/^https?:\/\//, '');
    if (param.suffix) {
      const escaped = param.suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      v = v.replace(new RegExp(escaped + '.*$'), '');
    }
    v = v.split('/')[0];
    v = v.split('?')[0];
    return v;
  };

  const handlePreAuthSubmit = () => {
    if (!preAuthConnector?.preAuthParams) return;

    const errors: Record<string, string> = {};
    const normalized: Record<string, string> = {};

    for (const param of preAuthConnector.preAuthParams) {
      const raw = preAuthValues[param.key] || '';
      const val = normalizePreAuthValue(raw, param);
      normalized[param.key] = val;

      if (param.required && !val) {
        errors[param.key] = `${param.label} is required`;
      } else if (param.pattern && val && !new RegExp(param.pattern).test(val)) {
        errors[param.key] = param.patternError || `Invalid ${param.label}`;
      }
    }

    if (Object.keys(errors).length > 0) {
      setPreAuthErrors(errors);
      return;
    }

    // v3.95 - Open OAuth popup immediately on button click (must be synchronous with gesture)
    setPreAuthModalOpen(false);
    setConnecting(preAuthConnector.connectorType);
    const popup = connectionsService.startOAuthPopup(preAuthConnector.connectorType, tenantId, normalized);
    popupRef.current = popup;
    setPopupStale(false);
    if (popupTimerRef.current) { clearTimeout(popupTimerRef.current); popupTimerRef.current = null; }
    if (popup) {
      const checkInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkInterval);
          setPopupStale(false);
          if (popupTimerRef.current) { clearTimeout(popupTimerRef.current); popupTimerRef.current = null; }
          setConnecting(prev => prev === preAuthConnector.connectorType ? null : prev);
        }
      }, 1000);
      popupTimerRef.current = setTimeout(() => {
        if (!popup.closed) setPopupStale(true);
      }, 30000);
    }
    setPreAuthConnector(null);
  };

  // v3.85 - MKTG-03: replaced mailto:support with ContactFormModal
  const handleRequestConnector = () => {
    setConnectorRequestOpen(true);
  };

  // v2.34 - Handle Finish Setup click - triggers data pipeline
  // v2.44 - Updated for full pipeline orchestration (no jobId needed)
  // v2.47 - Improved error handling with detailed logging - 2026-02-03
  // v4.10 - PIPELINE-ORCHESTRATOR-SYNC-AWAIT-01: handle 409 not-ready with per-reason
  //         auto-retry. Closes 6bdf59db race — fast Finish-Setup click during Airbyte
  //         setup now waits politely instead of crashing the pipeline. (Bumped from
  //         v4.09 — that tag was claimed in parallel by WRANGLING-L3 / Mobile.) - 2026-04-29
  // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-6a/b/c, F82): on not_ready the connection list is re-fetched and the
  //         shared helper decides from the pending connections' verdicts (precedence needs_attention >
  //         setting_up > syncing > unknown, never row order); needs_attention stops retrying even on a pending
  //         reason; AIRBYTE_SYNC_FAILED is a non-retry reason. Retry caps and the 5 s wait are unchanged.
  const handleFinishSetup = async () => {
    setFinishingSetup(true);
    setFinishError(null);
    setFinishPending([]);

    const POLL_INTERVAL_MS = 5000;
    const startedAt = Date.now();
    let lastReason: string | null = null;

    try {
      while (true) {
        const result: any = await setupService.complete();

        if (result.status === 'success') {
          console.log('v4.10: Setup complete success, calling onConnect()');
          onConnect();
          return;
        }

        if (result.status === 'not_ready') {
          lastReason = result.reason || 'UNKNOWN';
          let rows: HealthCarrier[] = [];
          if ((result.connectionsPending || []).length > 0) {
            try {
              rows = ((await connectionsService.list(tenantId)).connections || []) as HealthCarrier[];
            } catch (e) {
              // the pending connections render as "Status unknown"
            }
          }
          const decision = finishSetupDecision(result, rows, Date.now() - startedAt);
          if (decision.action === 'stop') {
            setFinishError(decision.message);
            setFinishPending(decision.pending);
            // v4.26 - INDUSTRY-CODE-NOT-STORED S8: no stored industry code -> the page shows the industry screen
            if (lastReason === 'INDUSTRY_CODE_MISSING') {
              onNeedIndustry?.();
            }
            return;
          }

          // Polite wait, then retry. UI keeps the spinner up.
          console.log(`v4.10: setup not ready (${lastReason}); retrying in ${POLL_INTERVAL_MS}ms`);
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          continue;
        }

        // Defensive: any other status string from the backend (legacy paths)
        const errorMsg = result.message || result.error || 'Failed to start data processing. Please try again.';
        setFinishError(errorMsg);
        return;
      }
    } catch (err: any) {
      console.error('v4.10: Finish setup failed:', err, 'lastReason=', lastReason);
      const errorMsg = err.userMessage || err.message || err.error || 'Failed to start data processing. Please try again.';
      setFinishError(errorMsg);
    } finally {
      setFinishingSetup(false);
    }
  };

  // v2.23 - Render a connector card with connection status
  const renderConnectorCard = (
    connector: ConnectorInfo,
    size: 'large' | 'medium' | 'small'
  ) => {
    const iconSize = size === 'large' ? 48 : size === 'medium' ? 36 : 28;
    const cardPadding = size === 'large' ? 3 : size === 'medium' ? 2 : 1.5;
    const titleVariant = size === 'large' ? 'h6' : size === 'medium' ? 'subtitle1' : 'body2';
    const showDescription = size !== 'small';

    // v2.23 - Check connector and connection status
    const isConnected = connector.connection !== null;
    const isComingSoon = connector.connectorStatus === 'coming_soon';
    const isMaintenance = connector.connectorStatus === 'maintenance';
    const isBeta = connector.connectorStatus === 'beta';
    // v3.61 - DEMO-01: one code path — demo limit blocks unconnected connectors when limit reached
    const isDisabled = isComingSoon || isMaintenance || connecting !== null
      || (demoLimitReached && !isConnected);

    // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-3b): `isConnected` keeps its meaning (a connection exists) for layout
    // and the demo limit; the badge, caption and border come from the verdict — green only for healthy.
    const connectionHealth = isConnected
      ? healthDisplay(connector.connection?.healthState, connector.connection?.healthReason)
      : null;

    const cardEl = (
      <Card
        key={connector.connectorType}
        sx={{
          height: '100%',
          border: '1px solid',
          borderColor: connectionHealth?.color === 'success' ? 'success.main'
            : connectionHealth?.color === 'error' ? 'error.main' : 'divider',
          opacity: isComingSoon || isMaintenance || (demoLimitReached && !isConnected) ? 0.6 : 1,
          '&:hover': {
            borderColor: isDisabled ? undefined : 'primary.main',
            boxShadow: isDisabled ? undefined : 2,
          },
        }}
      >
        <CardActionArea
          onClick={() => handleConnect(connector)}
          disabled={isDisabled || !canManageConnections}
          sx={{ height: '100%', p: cardPadding }}
        >
          <CardContent sx={{ textAlign: 'center', p: size === 'small' ? 1 : 2 }}>
            {/* v2.23 - Status badges */}
            <Box sx={{ position: 'relative', minHeight: 20, mb: 0.5 }}>
              {connectionHealth && (
                <Chip
                  icon={healthBadgeIcon(connectionHealth.state)}
                  label={connectionHealth.label}
                  size="small"
                  color={connectionHealth.color}
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              )}
              {isComingSoon && (
                <Chip
                  label="Coming Soon"
                  size="small"
                  sx={{ fontSize: '0.7rem', height: 20, bgcolor: 'grey.300' }}
                />
              )}
              {isMaintenance && (
                <Chip
                  label="Maintenance"
                  size="small"
                  color="warning"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              )}
              {isBeta && !isConnected && (
                <Chip
                  label="Beta"
                  size="small"
                  color="info"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              )}
              {/* v3.60 - Industry Fit chip: shown when connector has a non-zero NAICS priority score */}
              {!isConnected && !isComingSoon && !isMaintenance && (connector.priorityScore ?? 0) > 0 && (
                <Chip
                  label="Industry Fit"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              )}
            </Box>

            <Box
              sx={{
                color: isComingSoon || isMaintenance ? 'text.disabled' : 'primary.main',
                mb: size === 'small' ? 0.5 : 1,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {connecting === connector.connectorType ? (
                <CircularProgress size={iconSize} />
              ) : (
                getIcon(connector.icon, iconSize)
              )}
            </Box>
            <Typography
              variant={titleVariant}
              sx={{
                fontWeight: 600,
                color: isComingSoon || isMaintenance ? 'text.disabled' : 'text.primary'
              }}
            >
              {connector.displayName}
            </Typography>
            {showDescription && (
              <Typography variant="body2" color="text.secondary">
                {connector.description}
              </Typography>
            )}

            {/* v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-3b, F17): caption = the reason of a connection that needs attention */}
            {connectionHealth?.reason && size !== 'small' && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'error.main' }}>
                {connectionHealth.reason}
              </Typography>
            )}

            {/* v2.23 - Show company name if connected */}
            {isConnected && connector.connection?.companyName && size === 'large' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {connector.connection.companyName}
              </Typography>
            )}

          </CardContent>
        </CardActionArea>
      </Card>
    );

    // v3.61 - DEMO-01: wrap with Tooltip when demo limit is reached for unconnected connectors
    if (demoLimitReached && !isConnected) {
      return (
        <Tooltip
          title={`Demo limited to ${demoMaxConnections} connection${demoMaxConnections !== 1 ? 's' : ''}. Sign up to connect more.`}
          arrow
        >
          <span style={{ display: 'block', height: '100%' }}>{cardEl}</span>
        </Tooltip>
      );
    }
    return cardEl;
  };

  // Loading skeleton
  if (loading) {
    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Skeleton variant="rounded" height={140} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button onClick={loadConnectors}>Retry</Button>
      </Box>
    );
  }

  // Calculate "other" count for display
  const otherCount = Object.values(other).reduce((sum, arr) => sum + arr.length, 0);

  // v2.23 - Count connected sources
  const connectedCount = [...matched, ...recommended, ...Object.values(other).flat()]
    .filter(c => c.connection !== null).length;

  // v3.61 - DEMO-01: one-code-path limit — same slice/disable, same catalog
  const demoLimitReached = isDemo && connectedCount >= demoMaxConnections;

  return (
    <Box>
      {/* v2.23 - Summary of connected sources */}
      {connectedCount > 0 && (
        <Alert severity="success" sx={{ mb: 3 }}>
          You have {connectedCount} data source{connectedCount > 1 ? 's' : ''} connected.
        </Alert>
      )}

      {/* Section 1: Matched - Your Data Sources (Large icons) */}
      {matched.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
            Your Data Sources
          </Typography>
          <Grid container spacing={2}>
            {matched.map((connector) => (
              <Grid item xs={12} sm={6} key={connector.connectorType}>
                {renderConnectorCard(connector, 'large')}
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Section 2: Recommended - Popular for Your Business (Medium icons) */}
      {recommended.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
            Popular for Your Business
          </Typography>
          <Grid container spacing={2}>
            {recommended.map((connector) => (
              <Grid item xs={6} sm={4} key={connector.connectorType}>
                {renderConnectorCard(connector, 'medium')}
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Section 3: Other Data Sources (Small icons, collapsible) */}
      {otherCount > 0 && (
        <Box sx={{ mb: 3 }}>
          <Button
            onClick={() => setShowOther(!showOther)}
            endIcon={showOther ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ mb: 1, color: 'text.secondary' }}
          >
            {showOther ? 'Hide' : 'Show'} Other Data Sources ({otherCount})
          </Button>

          <Collapse in={showOther}>
            {Object.entries(other).map(([category, connectors]) => (
              <Box key={category} sx={{ mb: 2 }}>
                <Chip
                  label={categoryLabels[category] || category}
                  size="small"
                  sx={{ mb: 1 }}
                />
                <Grid container spacing={1}>
                  {connectors.map((connector) => (
                    <Grid item xs={4} sm={3} md={2} key={connector.connectorType}>
                      {renderConnectorCard(connector, 'small')}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Collapse>
        </Box>
      )}

      {/* No connectors state */}
      {matched.length === 0 && recommended.length === 0 && otherCount === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No connectors available at this time. Please contact support.
        </Alert>
      )}

      {/* v3.15 - Fix #3 resilience: stale popup banner (only shows after 30s with no response) */}
      {popupStale && connecting && (
        <Alert severity="warning" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" onClick={() => {
            // Close the stale popup and retry
            if (popupRef.current && !popupRef.current.closed) {
              popupRef.current.close();
            }
            setPopupStale(false);
            setConnecting(null);
            if (popupTimerRef.current) { clearTimeout(popupTimerRef.current); popupTimerRef.current = null; }
          }}>
            Close & Retry
          </Button>
        }>
          Taking too long? The connection window may have encountered an issue. You can close it and try again.
        </Alert>
      )}

      {/* v2.34 - Finish Setup error */}
      {finishError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {finishError}
          {/* v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-6b): each pending connection with its verdict */}
          {finishPending.length > 0 && (
            <Box component="ul" sx={{ m: 0, mt: 1, pl: 2 }}>
              {finishPending.map(p => {
                const name = [...matched, ...recommended, ...Object.values(other).flat()]
                  .find(c => c.connectorType === p.label)?.displayName || p.label;
                return (
                  <li key={p.id}>
                    <strong>{name}</strong>: {p.healthLabel}{p.healthReason ? ` — ${p.healthReason}` : ''}
                  </li>
                );
              })}
            </Box>
          )}
        </Alert>
      )}

      {/* v2.34 - Finish Setup button (shown when at least one source connected) */}
      {connectedCount > 0 && canRunPipeline && (
        <Box sx={{ textAlign: 'center', mt: 4, mb: 2 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleFinishSetup}
            disabled={finishingSetup}
            startIcon={finishingSetup ? <CircularProgress size={20} color="inherit" /> : <RocketLaunchIcon />}
            sx={{
              bgcolor: 'accent.main',
              color: 'white',
              px: 6,
              py: 1.5,
              fontSize: '1.1rem',
              '&:hover': { bgcolor: 'accent.dark' },
              '&:disabled': { bgcolor: 'grey.400' }
            }}
          >
            {finishingSetup ? 'Starting...' : 'Finish Setup'}
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            We'll start analyzing your data
          </Typography>
        </Box>
      )}

      {/* Request a Connector and Skip buttons */}
      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleRequestConnector}
          sx={{ mr: 2 }}
        >
          Request a Connector
        </Button>
        {connectedCount === 0 && (
          <Button
            variant="text"
            onClick={onSkip}
            sx={{ color: 'text.secondary' }}
          >
            Skip for now - I'll connect later
          </Button>
        )}
      </Box>

      {/* v3.85 - MKTG-03: Connector request modal (replaces mailto:support) */}
      <ContactFormModal
        open={connectorRequestOpen}
        onClose={() => setConnectorRequestOpen(false)}
        title="Request a Connector"
        initialMessage="Hi, I'd like to request a connector for: "
      />

      {/* v2.26 - Credential Form Modal for API Key / Basic Auth */}
      <CredentialFormModal
        open={credentialModalOpen}
        onClose={() => {
          setCredentialModalOpen(false);
          setCredentialFormSchema(null);
          setCredentialConnector(null);
          setCredentialError(null);
        }}
        formSchema={credentialFormSchema}
        connectorDisplayName={credentialConnector?.displayName || ''}
        onSubmit={handleCredentialSubmit}
        onDelegateClick={handleDelegateFromForm}
        loading={credentialLoading}
        error={credentialError}
      />

      {/* v2.26 - Delegation Modal */}
      <DelegationModal
        open={delegationModalOpen}
        onClose={() => {
          setDelegationModalOpen(false);
          setDelegationConnector(null);
        }}
        connectorType={delegationConnector?.connectorType || ''}
        connectorDisplayName={delegationConnector?.displayName || ''}
        onSendRequest={handleSendDelegation}
      />

      {/* v3.95 - Pre-Auth Modal: collect required params before OAuth start - 2026-04-06 */}
      <Dialog
        open={preAuthModalOpen}
        onClose={() => { setPreAuthModalOpen(false); setPreAuthConnector(null); setConnecting(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Connect {preAuthConnector?.displayName || ''}
        </DialogTitle>
        <DialogContent>
          {preAuthConnector?.preAuthParams?.map((param) => (
            <TextField
              key={param.key}
              label={param.label}
              placeholder={param.placeholder}
              value={preAuthValues[param.key] || ''}
              onChange={(e) => {
                setPreAuthValues(prev => ({ ...prev, [param.key]: e.target.value }));
                setPreAuthErrors(prev => { const n = { ...prev }; delete n[param.key]; return n; });
              }}
              error={!!preAuthErrors[param.key]}
              helperText={preAuthErrors[param.key]}
              fullWidth
              margin="normal"
              autoFocus
              InputProps={param.suffix ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body2" color="text.secondary">{param.suffix}</Typography>
                  </InputAdornment>
                ),
              } : undefined}
              onKeyDown={(e) => { if (e.key === 'Enter') handlePreAuthSubmit(); }}
            />
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setPreAuthModalOpen(false); setPreAuthConnector(null); setConnecting(null); }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handlePreAuthSubmit}>
            Connect
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataSourceConnect;
